import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_ROUTES } from 'shared/constants/api'; 
import apiClient from 'shared/api/client'; 

export interface Role {
  id: number;
  name: string;
  description: string;
}

export interface User {
  id: number;
  login: string;
  email: string;
  is_active: boolean;
  is_superuser: boolean;
  is_verified: boolean;
  roles: Role[];
  family_name?: string | null;
  given_name?: string | null;
  middle_name?: string | null;
  counterparty_id?: number;
  counterparty?: {
    id: number;
    name: string;
    inn?: string | null;
  };
}

interface AuthContextProps {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  login: (username: string, password: string) => Promise<boolean>;
  loginWithEsiaCode: (code: string) => Promise<boolean>;
  loginWithToken: (token: string) => Promise<boolean>;
  logout: () => Promise<void>;
  fetchUser: () => Promise<void>;
}

/** Экспортируется, чтобы в тестах можно было подставить пользователя без сети. */
export const AuthContext = createContext<AuthContextProps | undefined>(undefined);

const mapUser = (data: Partial<User>): User => ({
  id: data.id ?? 0,
  login: data.login ?? '',
  email: data.email ?? '',
  is_active: data.is_active ?? false,
  is_superuser: data.is_superuser ?? false,
  is_verified: data.is_verified ?? false,
  roles: Array.isArray(data.roles)
    ? data.roles.map(role => ({
        id: role.id ?? 0,
        name: role.name ?? '',
        description: role.description ?? '',
      }))
    : [],
  family_name: data.family_name,
  given_name: data.given_name,
  middle_name: data.middle_name,
  counterparty_id: data.counterparty_id,
  counterparty: data.counterparty,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    return Boolean(localStorage.getItem('authToken'));
  });
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      fetchUser();
    } else {
      setIsAuthenticated(false);
      setIsLoading(false);
    }
  }, []);


  const fetchUser = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.get<Partial<User>>(API_ROUTES.auth.me);
      setUser(mapUser(response.data));
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Не удалось получить данные пользователя:', error);
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        handleLogout();
      } else {
        setIsLoading(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (username: string, password: string): Promise<boolean> => {
    const form = new URLSearchParams();
    form.append('grant_type', 'password');
    form.append('username', username);
    form.append('password', password);

    try {
      setIsLoading(true);
      const response = await axios.post(
        API_ROUTES.auth.login,
        form,
        {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        }
      );

      const token = response.data.access_token;
      localStorage.setItem('authToken', token);
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      await fetchUser();
      return true;
    } catch (error) {
      console.error('Ошибка авторизации:', error);
      setIsLoading(false);
      return false;
    }
  };

  const loginWithEsiaCode = async (code: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      const response = await axios.post(API_ROUTES.auth.esiaExchange, { code });
      const token = response.data.access_token;
      localStorage.setItem('authToken', token);
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      await fetchUser();
      return true;
    } catch (error) {
      console.error('Ошибка входа через ЕСИ:', error);
      setIsLoading(false);
      return false;
    }
  };

  // Логин по уже полученному внутреннему токену (вход по ЭЦП и т.п.)
  const loginWithToken = async (token: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      localStorage.setItem('authToken', token);
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      await fetchUser();
      return true;
    } catch (error) {
      console.error('Ошибка входа по токену:', error);
      setIsLoading(false);
      return false;
    }
  };

const handleLogout = () => {
  // Очищаем все возможные места хранения токена
  localStorage.removeItem('authToken');
  localStorage.removeItem('token');
  sessionStorage.removeItem('authToken'); 
  sessionStorage.removeItem('token');
  
  delete apiClient.defaults.headers.common['Authorization'];
  setIsAuthenticated(false);
  setUser(null);
  setIsLoading(false);
  navigate('/login');
};

  const logout = async () => {
    try {
      setIsLoading(true);
      await apiClient.post(API_ROUTES.auth.logout);
    } catch (error) {
      console.warn('Logout failed on server');
    } finally {
      handleLogout();
    }
  };

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      isLoading,
      user,
      login,
      loginWithEsiaCode,
      loginWithToken,
      logout,
      fetchUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextProps {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}